import _classCallCheck from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT license.
import { ReplayableAudioNode } from "../common.browser/Exports";
import { ArgumentNullError, ConnectionState, createNoDashGuid, Deferred, EventSource, MessageType, PromiseHelper } from "../common/Exports";
import { CancellationErrorCode, CancellationReason, PropertyId, RecognitionEventArgs, SessionEventArgs } from "../sdk/Exports";
import { AgentConfig, DynamicGrammarBuilder, RequestSession, SpeechContext, SpeechDetected } from "./Exports";
import { SpeechConnectionMessage } from "./SpeechConnectionMessage.Internal";
export var ServiceRecognizerBase = /*#__PURE__*/function () {
  function ServiceRecognizerBase(authentication, connectionFactory, audioSource, recognizerConfig, recognizer) {
    var _this = this;

    _classCallCheck(this, ServiceRecognizerBase);

    this.recognizeOverride = undefined;
    this.disconnectOverride = undefined;

    this.sendTelemetryData = function () {
      var telemetryData = _this.privRequestSession.getTelemetry(); // console.warn("Telem: " + telemetryData);


      if (ServiceRecognizerBase.telemetryDataEnabled !== true || _this.privIsDisposed || null === telemetryData) {
        return PromiseHelper.fromResult(true);
      }

      if (!!ServiceRecognizerBase.telemetryData) {
        try {
          ServiceRecognizerBase.telemetryData(telemetryData);
          /* tslint:disable:no-empty */
        } catch (_a) {}
      }

      return _this.fetchConnection().onSuccessContinueWith(function (connection) {
        return connection.send(new SpeechConnectionMessage(MessageType.Text, "telemetry", _this.privRequestSession.requestId, "application/json", telemetryData));
      });
    };

    this.receiveMessageOverride = undefined;

    this.receiveMessage = function (successCallback, errorCallBack) {
      return _this.fetchConnection().on(function (connection) {
        return connection.read().onSuccessContinueWithPromise(function (message) {
          if (_this.receiveMessageOverride !== undefined) {
            return _this.receiveMessageOverride();
          }

          if (_this.privIsDisposed || !_this.privRequestSession.isRecognizing) {
            // We're done.
            return PromiseHelper.fromResult(undefined);
          } // indicates we are draining the queue and it came with no message;


          if (!message) {
            if (!_this.privRequestSession.isRecognizing) {
              return PromiseHelper.fromResult(true);
            } else {
              return _this.receiveMessage(successCallback, errorCallBack);
            }
          }

          var connectionMessage = SpeechConnectionMessage.fromConnectionMessage(message);

          if (connectionMessage.requestId.toLowerCase() === _this.privRequestSession.requestId.toLowerCase()) {
            switch (connectionMessage.path.toLowerCase()) {
              case "turn.start":
                _this.privMustReportEndOfStream = true;
                break;

              case "speech.startdetected":
                var speechStartDetected = SpeechDetected.fromJSON(connectionMessage.textBody);
                var speechStartEventArgs = new RecognitionEventArgs(speechStartDetected.Offset, _this.privRequestSession.sessionId);

                if (!!_this.privRecognizer.speechStartDetected) {
                  _this.privRecognizer.speechStartDetected(_this.privRecognizer, speechStartEventArgs);
                }

                break;

              case "speech.enddetected":
                var json;

                if (connectionMessage.textBody.length > 0) {
                  json = connectionMessage.textBody;
                } else {
                  // If the request was empty, the JSON returned is empty.
                  json = "{ Offset: 0 }";
                }

                var speechStopDetected = SpeechDetected.fromJSON(json); // Only shrink the buffers for continuous recognition.
                // For single shot, the speech.phrase message will come after the speech.end and it should own buffer shrink.

                if (_this.privRecognizerConfig.isContinuousRecognition) {
                  _this.privRequestSession.onServiceRecognized(speechStopDetected.Offset + _this.privRequestSession.currentTurnAudioOffset);
                }

                var speechStopEventArgs = new RecognitionEventArgs(speechStopDetected.Offset + _this.privRequestSession.currentTurnAudioOffset, _this.privRequestSession.sessionId);

                if (!!_this.privRecognizer.speechEndDetected) {
                  _this.privRecognizer.speechEndDetected(_this.privRecognizer, speechStopEventArgs);
                }

                break;

              case "turn.end":
                _this.sendTelemetryData();

                if (_this.privRequestSession.isSpeechEnded && _this.privMustReportEndOfStream) {
                  _this.privMustReportEndOfStream = false;

                  _this.cancelRecognitionLocal(CancellationReason.EndOfStream, CancellationErrorCode.NoError, undefined, successCallback);
                }

                var sessionStopEventArgs = new SessionEventArgs(_this.privRequestSession.sessionId);

                _this.privRequestSession.onServiceTurnEndResponse(_this.privRecognizerConfig.isContinuousRecognition);

                if (!_this.privRecognizerConfig.isContinuousRecognition || _this.privRequestSession.isSpeechEnded) {
                  if (!!_this.privRecognizer.sessionStopped) {
                    _this.privRecognizer.sessionStopped(_this.privRecognizer, sessionStopEventArgs);
                  }

                  return PromiseHelper.fromResult(true);
                } else {
                  _this.fetchConnection().onSuccessContinueWith(function (connection) {
                    _this.sendSpeechContext(connection);
                  });
                }

              default:
                _this.processTypeSpecificMessages(connectionMessage, successCallback, errorCallBack);

            }
          }

          return _this.receiveMessage(successCallback, errorCallBack);
        });
      }, function (error) {});
    };

    this.sendSpeechContext = function (connection) {
      var speechContextJson = _this.speechContext.toJSON();

      if (speechContextJson) {
        return connection.send(new SpeechConnectionMessage(MessageType.Text, "speech.context", _this.privRequestSession.requestId, "application/json", speechContextJson));
      }

      return PromiseHelper.fromResult(true);
    };

    this.connectImplOverride = undefined;
    this.configConnectionOverride = undefined;
    this.fetchConnectionOverride = undefined;

    this.sendSpeechServiceConfig = function (connection, requestSession, SpeechServiceConfigJson) {
      // filter out anything that is not required for the service to work.
      if (ServiceRecognizerBase.telemetryDataEnabled !== true) {
        var withTelemetry = JSON.parse(SpeechServiceConfigJson);
        var replacement = {
          context: {
            system: withTelemetry.context.system
          }
        };
        SpeechServiceConfigJson = JSON.stringify(replacement);
      }

      if (SpeechServiceConfigJson) {
        // && this.privConnectionId !== this.privSpeechServiceConfigConnectionId) {
        _this.privSpeechServiceConfigConnectionId = _this.privConnectionId;
        return connection.send(new SpeechConnectionMessage(MessageType.Text, "speech.config", requestSession.requestId, "application/json", SpeechServiceConfigJson));
      }

      return PromiseHelper.fromResult(true);
    };

    this.sendAudio = function (audioStreamNode) {
      // NOTE: Home-baked promises crash ios safari during the invocation
      // of the error callback chain (looks like the recursion is way too deep, and
      // it blows up the stack). The following construct is a stop-gap that does not
      // bubble the error up the callback chain and hence circumvents this problem.
      // TODO: rewrite with ES6 promises.
      var deferred = new Deferred(); // The time we last sent data to the service.

      var nextSendTime = Date.now();
      var audioFormat = _this.privAudioSource.format; // Max amount to send before we start to throttle

      var fastLaneSizeMs = _this.privRecognizerConfig.parameters.getProperty("SPEECH-TransmitLengthBeforThrottleMs", "5000");

      var maxSendUnthrottledBytes = audioFormat.avgBytesPerSec / 1000 * parseInt(fastLaneSizeMs, 10);
      var startRecogNumber = _this.privRequestSession.recogNumber;

      var readAndUploadCycle = function readAndUploadCycle() {
        // If speech is done, stop sending audio.
        if (!_this.privIsDisposed && !_this.privRequestSession.isSpeechEnded && _this.privRequestSession.isRecognizing && _this.privRequestSession.recogNumber === startRecogNumber) {
          _this.fetchConnection().on(function (connection) {
            audioStreamNode.read().on(function (audioStreamChunk) {
              // we have a new audio chunk to upload.
              if (_this.privRequestSession.isSpeechEnded) {
                // If service already recognized audio end then don't send any more audio
                deferred.resolve(true);
                return;
              }

              var payload;
              var sendDelay;

              if (audioStreamChunk.isEnd) {
                payload = null;
                sendDelay = 0;
              } else {
                payload = audioStreamChunk.buffer;

                _this.privRequestSession.onAudioSent(payload.byteLength);

                if (maxSendUnthrottledBytes >= _this.privRequestSession.bytesSent) {
                  sendDelay = 0;
                } else {
                  sendDelay = Math.max(0, nextSendTime - Date.now());
                }
              } // Are we ready to send, or need we delay more?


              setTimeout(function () {
                if (payload !== null) {
                  nextSendTime = Date.now() + payload.byteLength * 1000 / (audioFormat.avgBytesPerSec * 2);
                }

                var uploaded = connection.send(new SpeechConnectionMessage(MessageType.Binary, "audio", _this.privRequestSession.requestId, null, payload));

                if (!audioStreamChunk.isEnd) {
                  uploaded.continueWith(function (_) {
                    // Regardless of success or failure, schedule the next upload.
                    // If the underlying connection was broken, the next cycle will
                    // get a new connection and re-transmit missing audio automatically.
                    readAndUploadCycle();
                  });
                } else {
                  // the audio stream has been closed, no need to schedule next
                  // read-upload cycle.
                  _this.privRequestSession.onSpeechEnded();

                  deferred.resolve(true);
                }
              }, sendDelay);
            }, function (error) {
              if (_this.privRequestSession.isSpeechEnded) {
                // For whatever reason, Reject is used to remove queue subscribers inside
                // the Queue.DrainAndDispose invoked from DetachAudioNode down below, which
                // means that sometimes things can be rejected in normal circumstances, without
                // any errors.
                deferred.resolve(true); // TODO: remove the argument, it's is completely meaningless.
              } else {
                // Only reject, if there was a proper error.
                deferred.reject(error);
              }
            });
          }, function (error) {
            deferred.reject(error);
          });
        }
      };

      readAndUploadCycle();
      return deferred.promise();
    };

    this.fetchConnection = function () {
      if (_this.fetchConnectionOverride !== undefined) {
        return _this.fetchConnectionOverride();
      }

      return _this.configureConnection();
    };

    if (!authentication) {
      throw new ArgumentNullError("authentication");
    }

    if (!connectionFactory) {
      throw new ArgumentNullError("connectionFactory");
    }

    if (!audioSource) {
      throw new ArgumentNullError("audioSource");
    }

    if (!recognizerConfig) {
      throw new ArgumentNullError("recognizerConfig");
    }

    this.privMustReportEndOfStream = false;
    this.privAuthentication = authentication;
    this.privConnectionFactory = connectionFactory;
    this.privAudioSource = audioSource;
    this.privRecognizerConfig = recognizerConfig;
    this.privIsDisposed = false;
    this.privRecognizer = recognizer;
    this.privRequestSession = new RequestSession(this.privAudioSource.id());
    this.privConnectionEvents = new EventSource();
    this.privDynamicGrammar = new DynamicGrammarBuilder();
    this.privSpeechContext = new SpeechContext(this.privDynamicGrammar);
    this.privAgentConfig = new AgentConfig();
  }

  _createClass(ServiceRecognizerBase, [{
    key: "isDisposed",
    value: function isDisposed() {
      return this.privIsDisposed;
    }
  }, {
    key: "dispose",
    value: function dispose(reason) {
      this.privIsDisposed = true;

      if (this.privConnectionConfigurationPromise) {
        this.privConnectionConfigurationPromise.onSuccessContinueWith(function (connection) {
          connection.dispose(reason);
        });
      }
    }
  }, {
    key: "recognize",
    value: function recognize(recoMode, successCallback, errorCallBack) {
      var _this2 = this;

      if (this.recognizeOverride !== undefined) {
        return this.recognizeOverride(recoMode, successCallback, errorCallBack);
      } // Clear the existing configuration promise to force a re-transmission of config and context.


      this.privConnectionConfigurationPromise = null;
      this.privRecognizerConfig.recognitionMode = recoMode;
      this.privRequestSession.startNewRecognition();
      this.privRequestSession.listenForServiceTelemetry(this.privAudioSource.events); // Start the connection to the service. The promise this will create is stored and will be used by configureConnection().

      this.connectImpl();
      return this.audioSource.attach(this.privRequestSession.audioNodeId).continueWithPromise(function (result) {
        var audioNode;

        if (result.isError) {
          _this2.cancelRecognitionLocal(CancellationReason.Error, CancellationErrorCode.ConnectionFailure, result.error, successCallback);

          return PromiseHelper.fromError(result.error);
        } else {
          audioNode = new ReplayableAudioNode(result.result, _this2.audioSource.format);

          _this2.privRequestSession.onAudioSourceAttachCompleted(audioNode, false);
        }

        return _this2.audioSource.deviceInfo.onSuccessContinueWithPromise(function (deviceInfo) {
          _this2.privRecognizerConfig.SpeechServiceConfig.Context.audio = {
            source: deviceInfo
          };
          return _this2.configureConnection().on(function (_) {
            var sessionStartEventArgs = new SessionEventArgs(_this2.privRequestSession.sessionId);

            if (!!_this2.privRecognizer.sessionStarted) {
              _this2.privRecognizer.sessionStarted(_this2.privRecognizer, sessionStartEventArgs);
            }

            var messageRetrievalPromise = _this2.receiveMessage(successCallback, errorCallBack);

            var audioSendPromise = _this2.sendAudio(audioNode);
            /* tslint:disable:no-empty */


            audioSendPromise.on(function (_) {}, function (error) {
              _this2.cancelRecognitionLocal(CancellationReason.Error, CancellationErrorCode.RuntimeError, error, successCallback);
            });
            var completionPromise = PromiseHelper.whenAll([messageRetrievalPromise, audioSendPromise]);
            return completionPromise.on(function (r) {
              return true;
            }, function (error) {
              _this2.cancelRecognitionLocal(CancellationReason.Error, CancellationErrorCode.RuntimeError, error, successCallback);
            });
          }, function (error) {
            _this2.cancelRecognitionLocal(CancellationReason.Error, CancellationErrorCode.ConnectionFailure, error, successCallback);
          }).continueWithPromise(function (result) {
            if (result.isError) {
              return PromiseHelper.fromError(result.error);
            } else {
              return PromiseHelper.fromResult(true);
            }
          });
        });
      });
    }
  }, {
    key: "stopRecognizing",
    value: function stopRecognizing() {
      if (this.privRequestSession.isRecognizing) {
        this.privRequestSession.onStopRecognizing();
        this.sendTelemetryData();
        this.audioSource.turnOff();
        this.sendFinalAudio();
        this.privRequestSession.dispose();
      }
    }
  }, {
    key: "connect",
    value: function connect() {
      this.connectImpl().result();
    }
  }, {
    key: "disconnect",
    value: function disconnect() {
      if (this.disconnectOverride !== undefined) {
        this.disconnectOverride();
        return;
      }

      this.cancelRecognitionLocal(CancellationReason.Error, CancellationErrorCode.NoError, "Disconnecting", undefined);

      if (this.privConnectionPromise.result().isCompleted) {
        if (!this.privConnectionPromise.result().isError) {
          this.privConnectionPromise.result().result.dispose();
          this.privConnectionPromise = null;
        }
      } else {
        this.privConnectionPromise.onSuccessContinueWith(function (connection) {
          connection.dispose();
        });
      }
    }
  }, {
    key: "sendMessage",
    value: function sendMessage(message) {} // Cancels recognition.

  }, {
    key: "cancelRecognitionLocal",
    value: function cancelRecognitionLocal(cancellationReason, errorCode, error, cancelRecoCallback) {
      if (!!this.privRequestSession.isRecognizing) {
        this.privRequestSession.onStopRecognizing();
        this.sendTelemetryData();
        this.cancelRecognition(this.privRequestSession.sessionId, this.privRequestSession.requestId, cancellationReason, errorCode, error, cancelRecoCallback);
      }
    } // Establishes a websocket connection to the end point.

  }, {
    key: "connectImpl",
    value: function connectImpl() {
      var _this3 = this;

      var isUnAuthorized = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

      if (this.connectImplOverride !== undefined) {
        return this.connectImplOverride(isUnAuthorized);
      }

      if (this.privConnectionPromise) {
        if (this.privConnectionPromise.result().isCompleted && (this.privConnectionPromise.result().isError || this.privConnectionPromise.result().result.state() === ConnectionState.Disconnected)) {
          this.privConnectionId = null;
          this.privConnectionPromise = null;
          return this.connectImpl();
        } else {
          return this.privConnectionPromise;
        }
      }

      this.privAuthFetchEventId = createNoDashGuid();
      this.privConnectionId = createNoDashGuid();
      this.privRequestSession.onPreConnectionStart(this.privAuthFetchEventId, this.privConnectionId);
      var authPromise = isUnAuthorized ? this.privAuthentication.fetchOnExpiry(this.privAuthFetchEventId) : this.privAuthentication.fetch(this.privAuthFetchEventId);
      this.privConnectionPromise = authPromise.continueWithPromise(function (result) {
        if (result.isError) {
          _this3.privRequestSession.onAuthCompleted(true, result.error);

          throw new Error(result.error);
        } else {
          _this3.privRequestSession.onAuthCompleted(false);
        }

        var connection = _this3.privConnectionFactory.create(_this3.privRecognizerConfig, result.result, _this3.privConnectionId);

        _this3.privRequestSession.listenForServiceTelemetry(connection.events); // Attach to the underlying event. No need to hold onto the detach pointers as in the event the connection goes away,
        // it'll stop sending events.


        connection.events.attach(function (event) {
          _this3.connectionEvents.onEvent(event);
        });
        return connection.open().onSuccessContinueWithPromise(function (response) {
          if (response.statusCode === 200) {
            _this3.privRequestSession.onPreConnectionStart(_this3.privAuthFetchEventId, _this3.privConnectionId);

            _this3.privRequestSession.onConnectionEstablishCompleted(response.statusCode);

            return PromiseHelper.fromResult(connection);
          } else if (response.statusCode === 403 && !isUnAuthorized) {
            return _this3.connectImpl(true);
          } else {
            _this3.privRequestSession.onConnectionEstablishCompleted(response.statusCode, response.reason);

            return PromiseHelper.fromError("Unable to contact server. StatusCode: ".concat(response.statusCode, ", ").concat(_this3.privRecognizerConfig.parameters.getProperty(PropertyId.SpeechServiceConnection_Endpoint), " Reason: ").concat(response.reason));
          }
        });
      });
      return this.privConnectionPromise;
    }
  }, {
    key: "sendFinalAudio",
    value: function sendFinalAudio() {
      var _this4 = this;

      var deferred = new Deferred();
      this.fetchConnection().on(function (connection) {
        connection.send(new SpeechConnectionMessage(MessageType.Binary, "audio", _this4.privRequestSession.requestId, null, null)).on(function (_) {
          deferred.resolve(true);
        }, function (error) {
          deferred.reject(error);
        });
      }, function (error) {
        deferred.reject(error);
      });
      return deferred.promise();
    } // Takes an established websocket connection to the endpoint and sends speech configuration information.

  }, {
    key: "configureConnection",
    value: function configureConnection() {
      var _this5 = this;

      if (this.configConnectionOverride !== undefined) {
        return this.configConnectionOverride();
      }

      if (this.privConnectionConfigurationPromise) {
        if (this.privConnectionConfigurationPromise.result().isCompleted && (this.privConnectionConfigurationPromise.result().isError || this.privConnectionConfigurationPromise.result().result.state() === ConnectionState.Disconnected)) {
          this.privConnectionConfigurationPromise = null;
          return this.configureConnection();
        } else {
          return this.privConnectionConfigurationPromise;
        }
      }

      this.privConnectionConfigurationPromise = this.connectImpl().onSuccessContinueWithPromise(function (connection) {
        return _this5.sendSpeechServiceConfig(connection, _this5.privRequestSession, _this5.privRecognizerConfig.SpeechServiceConfig.serialize()).onSuccessContinueWithPromise(function (_) {
          return _this5.sendSpeechContext(connection).onSuccessContinueWith(function (_) {
            return connection;
          });
        });
      });
      return this.privConnectionConfigurationPromise;
    }
  }, {
    key: "audioSource",
    get: function get() {
      return this.privAudioSource;
    }
  }, {
    key: "speechContext",
    get: function get() {
      return this.privSpeechContext;
    }
  }, {
    key: "dynamicGrammar",
    get: function get() {
      return this.privDynamicGrammar;
    }
  }, {
    key: "agentConfig",
    get: function get() {
      return this.privAgentConfig;
    }
  }, {
    key: "connectionEvents",
    get: function get() {
      return this.privConnectionEvents;
    }
  }, {
    key: "recognitionMode",
    get: function get() {
      return this.privRecognizerConfig.recognitionMode;
    }
  }]);

  return ServiceRecognizerBase;
}();
ServiceRecognizerBase.telemetryDataEnabled = true;